import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useListNotificationsQuery, useMarkNotificationReadMutation } from '@/services/api';
import { formatAge } from '@/utils/format';
import { useToast } from '@/components/toast/ToastProvider';
import { Meta } from '@/components/Meta';

function playAlert(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const emit = (): void => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1180, ctx.currentTime + 0.09);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.09, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.34);
      osc.onended = () => void ctx.close();
    };
    if (ctx.state === 'suspended') {
      void ctx.resume().then(emit).catch(() => void ctx.close());
    } else {
      emit();
    }
  } catch {
  }
}

let osPermissionAsked = false;

function raiseOsNotification(title: string, body: string): void {
  try {
    if (typeof Notification === 'undefined') return;
    if (!document.hidden) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, tag: 'aicf-notification' });
      return;
    }
    if (Notification.permission === 'denied' || osPermissionAsked) return;
    osPermissionAsked = true;
    void Notification.requestPermission().then((result) => {
      if (result === 'granted' && document.hidden) {
        new Notification(title, { body, tag: 'aicf-notification' });
      }
    });
  } catch {
  }
}

export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const [osPermission, setOsPermission] = useState<NotificationPermission | 'unsupported'>(() =>
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
  );
  const canAskOsPermission = osPermission === 'default';
  const { data } = useListNotificationsQuery(undefined, { pollingInterval: 30_000 });
  const [markRead] = useMarkNotificationReadMutation();
  const unread = data?.unread_count ?? 0;
  const items = (data?.notifications ?? []).slice(0, 8);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const seenIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    const all = data?.notifications;
    if (!all) return;
    const ids = all.map((n) => n.notification_id);
    if (seenIds.current === null) {
      seenIds.current = new Set(ids);
      return;
    }
    const fresh = all.filter((n) => !seenIds.current!.has(n.notification_id));
    for (const id of ids) seenIds.current.add(id);
    if (fresh.length === 0) return;
    const single = fresh.length === 1 ? fresh[0]! : null;
    const headline = single ? (single.title ?? 'New notification') : `${fresh.length} new notifications`;
    toast.info(headline);
    playAlert();
    raiseOsNotification(
      headline,
      single?.body ?? 'Open the notifications page for details.'
    );
  }, [data, toast]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: 'var(--radius, 8px)',
          background: open ? 'var(--surface-sunken)' : 'transparent',
          border: '1px solid transparent',
          cursor: 'pointer',
          color: 'var(--text)'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 2,
              right: 2,
              background: 'var(--red)',
              color: '#fff',
              borderRadius: 9,
              fontSize: 10,
              fontWeight: 600,
              minWidth: 15,
              height: 15,
              lineHeight: '15px',
              textAlign: 'center',
              padding: '0 3px',
              border: '1.5px solid var(--surface)'
            }}
          >
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 32,
            width: 340,
            maxHeight: 420,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50
          }}
        >
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 13 }}>
            Notifications
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>
              No notifications yet.
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.notification_id}
                role="button"
                tabIndex={0}
                data-testid="bell-notification-row"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('a,button')) return;
                  setOpen(false);
                  navigate('/notifications');
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  if ((e.target as HTMLElement).closest('a,button')) return;
                  e.preventDefault();
                  setOpen(false);
                  navigate('/notifications');
                }}
                style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--border)',
                  background: n.read_at ? 'transparent' : 'var(--surface-alt)',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--red)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}
                  >
                    SLA breached
                  </span>
                  {n.project_name && (
                    <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.project_name}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                    {formatAge(n.created_at)} ago
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{n.body}</div>
                {(n.pipeline_run_id || n.stage_role) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 6px', marginTop: 5 }}>
                    {n.pipeline_run_id && <Meta label="run" value={n.pipeline_run_id} mono />}
                    {n.stage_role && <Meta label="stage" value={n.stage_role} />}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  {!n.read_at && (
                    <button
                      type="button"
                      style={{ fontSize: 11, background: 'none', border: 'none', color: 'var(--text)', cursor: 'pointer', padding: 0 }}
                      onClick={() => void markRead(n.notification_id)}
                    >
                      Mark read
                    </button>
                  )}
                  {n.project_slug && n.pipeline_run_id && (
                    <Link
                      className="btn sm"
                      style={{ marginLeft: 'auto' }}
                      to={`/p/${n.project_slug}/runs/${encodeURIComponent(n.pipeline_run_id)}`}
                      onClick={() => {
                        if (!n.read_at) void markRead(n.notification_id);
                        setOpen(false);
                      }}
                      data-testid="bell-view-run"
                    >
                      View run →
                    </Link>
                  )}
                </div>
              </div>
            ))
          )}
          <Link
            to="/notifications"
            style={{ display: 'block', padding: '10px 14px', fontSize: 12, textAlign: 'center', color: 'var(--text)' }}
            onClick={() => setOpen(false)}
          >
            See all notifications
          </Link>
          {canAskOsPermission && (
            <button
              type="button"
              onClick={() => {
                playAlert();
                void Notification.requestPermission().then((r) => {
                  setOsPermission(r);
                  toast.info(
                    r === 'granted'
                      ? 'Desktop alerts on — new notifications will show even when this tab is in the background.'
                      : 'Desktop alerts stay off. You will still see the in-app toast and the bell badge.'
                  );
                });
              }}
              style={{
                display: 'block', width: '100%', padding: '9px 14px',
                borderTop: '1px solid var(--border)', background: 'none', border: 'none',
                borderTopStyle: 'solid', fontSize: 11.5, color: 'var(--text-muted)',
                cursor: 'pointer', textAlign: 'center'
              }}
            >
              🔔 Enable desktop alerts
            </button>
          )}
        </div>
      )}
    </div>
  );
}
